import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import { CVI, GVI, NOT_APPLICABLE_LABEL } from '@/constants';
import { localiseMeasurement } from '@/utils/unitSystem';
import {
  AllPointOfInterestsForDataLoaderQuery,
  UnitsEnum,
  useAllPointOfInterestsForDataLoaderQuery,
} from '@/__generated__/graphql';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import useLiveSyncAssemblyPois from '../../shared/hooks/useLiveSyncAssemblyPois';

export const useFilterCviAndGviPois = () => {
  const structureId = useGetStructureId();
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const [allTemplates, setAllTemplates] = useAtom(state.allPointOfInterestTemplates);
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const setIsLoadingPOIsForReviewReport = useSetAtom(state.loadingPOIsForReviewReport);

  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();

  useEffect(() => {
    if (allPointOfInterestTemplates?.allPointOfInterestTemplate) {
      setAllTemplates(allPointOfInterestTemplates?.allPointOfInterestTemplate);
    }
  }, [allPointOfInterestTemplates?.allPointOfInterestTemplate, setAllTemplates]);

  const {
    data: poisData,
    loading,
    refetch,
  } = useAllPointOfInterestsForDataLoaderQuery({
    variables: {
      input: {
        structureId,
        assemblyIds: [selectedAssemblyId ?? ''],
      },
    },
  });

  useLiveSyncAssemblyPois(() => {
    setIsLoadingPOIsForReviewReport(true);
    refetch().then(() => setIsLoadingPOIsForReviewReport(false));
  });

  const reviewActionIds = useMemo(() => {
    return allTemplates.flatMap(
      (item) =>
        item.fields?.filter((field) => field.key === 'review_action').map((field) => field.id) || []
    );
  }, [allTemplates]);

  const { cviPois, gviPois } = useMemo(() => {
    if (!poisData || !reviewActionIds) return { cviPois: [], gviPois: [] };

    const cvi: typeof poisData.pointOfInterests = [];
    const gvi: typeof poisData.pointOfInterests = [];

    for (const poi of poisData.pointOfInterests) {
      const matchingEntry = poi.templateData?.find(
        (templateData) =>
          reviewActionIds.includes(templateData.id) &&
          (templateData.value?.toUpperCase() === CVI || templateData.value?.toUpperCase() === GVI)
      );

      if (!matchingEntry) continue;

      if (matchingEntry.value?.toUpperCase() === CVI) {
        cvi.push(poi);
      }

      if (matchingEntry.value?.toUpperCase() === GVI) {
        gvi.push(poi);
      }
    }

    return { cviPois: cvi, gviPois: gvi };
  }, [poisData, reviewActionIds]);

  const scCategoryIds = useMemo(() => {
    return allTemplates.flatMap(
      (item) =>
        item.fields?.filter((field) => field.key === 'sc_category').map((field) => field.id) || []
    );
  }, [allTemplates]);

  const transformPois = useCallback(
    (
      pois: AllPointOfInterestsForDataLoaderQuery['pointOfInterests']
    ): Record<string, string | number>[] => {
      return pois.map((poi) => ({
        'POI ID': poi.poiId ?? NOT_APPLICABLE_LABEL,
        'POI Type': poi.type ?? NOT_APPLICABLE_LABEL,
        'SC Category':
          poi.templateData?.find((data) => scCategoryIds?.includes(data.id))?.value?.trim() ||
          NOT_APPLICABLE_LABEL,
        'Height from Deck':
          localiseMeasurement({
            unitSystem: unitConversionSystem,
            value: poi.deckRelativeHeight,
            sourceUnit: UnitsEnum.Meter,
            roundToDecimal: 1,
            fallbackLabel: NOT_APPLICABLE_LABEL,
          }) ?? NOT_APPLICABLE_LABEL,
        'Paint Region': poi.paintRegion?.name ?? NOT_APPLICABLE_LABEL,
      }));
    },
    [scCategoryIds, unitConversionSystem]
  );

  const filteredCviPois = useMemo(
    () => ({
      title: 'CVI Checklist',
      data: transformPois(cviPois),
    }),
    [cviPois, transformPois]
  );

  const filteredGviPois = useMemo(
    () => ({
      title: 'GVI Checklist',
      data: transformPois(gviPois),
    }),
    [gviPois, transformPois]
  );

  const allPois = useMemo(
    () => [filteredCviPois, filteredGviPois],
    [filteredCviPois, filteredGviPois]
  );

  const headers = useMemo(() => {
    return Array.from(
      new Set(allPois.flatMap((group) => group.data).flatMap((row) => Object.keys(row)))
    );
  }, [allPois]);

  return {
    cviPois,
    gviPois,
    filteredCviPois,
    filteredGviPois,
    allPois,
    loading,
    headers,
  };
};
